import React, { useState, useEffect } from "react";
import axios from "axios";
import "../css/editemployeeRoutes.css";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "./CustomButton";
import formatDate from "../utils/formatDate";
import { hasAccess } from "../utils/access";
const EditEmployeeRoutes = () => {
  const isExpenseUser = hasAccess("dashboard_expense");;
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [routes, setRoutes] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [editableRow, setEditableRow] = useState(null);
  const [editedData, setEditedData] = useState({
    route_name: "",
    state_id: "",
    city_id: "",
    headquarter_id: "",
    route_type_id: "",
    distance_from_hq: 0,
  });

  const [allCities, setAllCities] = useState([]);
  const [allHeadquarters, setAllHeadquarters] = useState([]);
  const [allRouteTypes, setAllRouteTypes] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [filteredHeadquarters, setFilteredHeadquarters] = useState([]);
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [activeOperation, setActiveOperation] = useState(null);
  const [mergePreview, setMergePreview] = useState(null);
  const [mergedRouteData, setMergedRouteData] = useState({
    route_name: "",
    state_id: "",
    city_id: "",
    headquarter_id: "",
    route_type_id: "",
    active: true,
    distance_from_hq: 0,
  });
  const [transferMode, setTransferMode] = useState(false);
  const [selectedTransferRoutes, setSelectedTransferRoutes] = useState([]);
  const [filteredFromEmployees, setFilteredFromEmployees] = useState([]);
  const [filteredToDivisions, setFilteredToDivisions] = useState([]);
  const [filteredToEmployees, setFilteredToEmployees] = useState([]);
  const [transferData, setTransferData] = useState({
    fromState: "",
    fromDivision: "",
    fromEmployee: "",
    toState: "",
    toDivision: "",
    toEmployee: "",
  });

  const [copyMode, setCopyMode] = useState(false);
  const [copyData, setCopyData] = useState({
    withCustomers: true,
    toEmployee: "",
    toDivision: "",
    toState: "",
  });
  const [selectedCopyRoutes, setSelectedCopyRoutes] = useState([]);
  const [selectedDeleteRoutes, setSelectedDeleteRoutes] = useState([]);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);

  const handleDeleteCheckbox = (routeId) => {
    if (activeOperation && activeOperation !== "delete") return;

    setSelectedDeleteRoutes((prev) => {
      const newSelection = prev.includes(routeId)
        ? prev.filter((id) => id !== routeId)
        : [...prev, routeId];

      if (newSelection.length === 0) {
        setActiveOperation(null);
      } else {
        setActiveOperation("delete");
      }

      return newSelection;
    });
  };
  const totalDoctors = routes.reduce(
    (sum, r) => sum + Number(r.doctor_count || 0),
    0,
  );

  const totalChemists = routes.reduce(
    (sum, r) => sum + Number(r.chemist_count || 0),
    0,
  );

  const totalStockists = routes.reduce(
    (sum, r) => sum + Number(r.stockist_count || 0),
    0,
  );

  const handleSelectAllDeleteRoutes = (e) => {
    if (activeOperation && activeOperation !== "delete") return;

    if (e.target.checked) {
      setSelectedDeleteRoutes(routes.map((route) => route.id));
      setActiveOperation("delete");
    } else {
      setSelectedDeleteRoutes([]);
      setActiveOperation(null);
    }
  };

  const handleDeleteRoutes = async () => {
    if (!selectedDeleteRoutes.length) {
      alert("Please select at least one route to delete");
      return;
    }

    try {
      await axios.delete(`${BASE_URL}/api/routes/bulk-delete`, {
        data: { route_ids: selectedDeleteRoutes },
        headers: getAuthHeaders(),
      });

      alert(`Successfully deleted ${selectedDeleteRoutes.length} routes`);
      setSelectedDeleteRoutes([]);
      setActiveOperation(null);
      setDeleteConfirmation(false);
      handleGoClick();
    } catch (error) {
      console.error("Delete error:", error);
      alert(`Delete failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleCancelDelete = () => {
    setSelectedDeleteRoutes([]);
    setActiveOperation(null);
    setDeleteConfirmation(false);
  };

  const handleCopyCheckbox = (routeId) => {
    if (activeOperation && activeOperation !== "copy") return;

    setSelectedCopyRoutes((prev) => {
      const newSelection = prev.includes(routeId)
        ? prev.filter((id) => id !== routeId)
        : [...prev, routeId];

      if (newSelection.length === 0) {
        setCopyMode(false);
        setActiveOperation(null);
      } else {
        setCopyMode(true);
        setActiveOperation("copy");
      }

      return newSelection;
    });
  };

  const handleCopyRoutes = async () => {
    if (!selectedCopyRoutes.length || !copyData.toEmployee) {
      alert("Please select routes and destination employee");
      return;
    }
    try {
      const response = await axios.post(
        `${BASE_URL}/api/routes/copy`,
        {
          route_ids: selectedCopyRoutes,
          to_employee_id: copyData.toEmployee,
          with_customers: copyData.withCustomers,
          to_division: copyData.toDivision,
          to_state: copyData.toState,
        },
        {
          headers: getAuthHeaders(),
        },
      );

      alert(`Successfully copied ${response.data.copied_routes.length} routes`);
      setCopyMode(false);
      setSelectedCopyRoutes([]);
      setActiveOperation(null);
      handleGoClick();
    } catch (error) {
      console.error("Copy error:", error);
      alert(`Copy failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  useEffect(() => {
    if (!copyMode) {
      setSelectedCopyRoutes([]);
    }
  }, [copyMode]);

  const handleCancelCopy = () => {
    setCopyMode(false);
    setSelectedCopyRoutes([]);
    setActiveOperation(null);
    setCopyData({
      withCustomers: true,
      toEmployee: "",
      toDivision: "",
      toState: "",
    });
    setFilteredToEmployees([]);
  };

  const handleTransferCheckbox = (routeId) => {
    if (activeOperation && activeOperation !== "transfer") return;

    setSelectedTransferRoutes((prev) => {
      const newSelection = prev.includes(routeId)
        ? prev.filter((id) => id !== routeId)
        : [...prev, routeId];

      if (newSelection.length > 0) {
        setActiveOperation("transfer");
        setTransferMode(true);
      } else {
        setActiveOperation(null);
        setTransferMode(false);
      }

      return newSelection;
    });
  };

  const handleSelectAllTransferRoutes = (e) => {
    if (activeOperation && activeOperation !== "transfer") return;

    if (e.target.checked) {
      setSelectedTransferRoutes(routes.map((route) => route.id));
      setActiveOperation("transfer");
      setTransferMode(true);
    } else {
      setSelectedTransferRoutes([]);
      setActiveOperation(null);
      setTransferMode(false);
    }
  };

  const fetchRouteDetails = async (routeId) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/routes/${routeId}`, {
        headers: getAuthHeaders(),
      });
      const route = response.data;

      setTransferData({
        state: route.state_id,
        fromDivision: route.division_name,
        fromDivisionId: route.division_id,
        fromEmployee: route.employee_id,
        toDivision: "",
        toEmployee: "",
      });

      if (route.state_id) {
        const divisionsRes = await axios.get(
          `${BASE_URL}/api/divisions?state_id=${route.state_id}`,
          {
            headers: getAuthHeaders(),
          },
        );
        setDivisions(divisionsRes.data);
      }

      if (route.division) {
        const employeesRes = await axios.get(
          `${BASE_URL}/api/employees?state_id=${route.state_id}&division=${route.division}`,
          { headers: getAuthHeaders() },
        );
        setFilteredFromEmployees(employeesRes.data);
      }
    } catch (error) {
      console.error("Error fetching route details:", error);
    }
  };

  const initializeTransferData = (route, divisionList) => {
    const division =
      divisionList.find((d) => d.id === route.division_id)?.name || "";

    setTransferData({
      state: route.state_id,
      fromDivision: division,
      fromEmployee: route.employee_id,
      toState: route.state_id,
      toDivision: "",
      toEmployee: "",
    });

    if (division && route.state_id) {
      axios
        .get(
          `${BASE_URL}/api/employees?state_id=${
            route.state_id
          }&division=${encodeURIComponent(division)}`,
          { headers: getAuthHeaders() },
        )
        .then((res) => setFilteredFromEmployees(res.data))
        .catch((err) => console.error("Error fetching employees:", err));
    }

    setFilteredToDivisions(
      divisionList.filter((d) => d.state_id === route.state_id),
    );
  };

  const handleTransferInputChange = (e, field) => {
    const value = e.target.value;
    const newData = { ...transferData, [field]: value };

    if (field === "toState") {
      axios
        .get(`${BASE_URL}/api/divisions?state_id=${value}`, {
          headers: getAuthHeaders(),
        })
        .then((res) => {
          setFilteredToDivisions(res.data);
          newData.toDivision = "";
          newData.toEmployee = "";
          setTransferData(newData);
        })
        .catch((err) => console.error("Error fetching divisions:", err));
      return;
    } else if (field === "toDivision") {
      newData.toEmployee = "";
      if (value && newData.toState) {
        axios
          .get(
            `${BASE_URL}/api/employees?state_id=${
              newData.toState
            }&division=${encodeURIComponent(value)}`,
            {
              headers: getAuthHeaders(),
            },
          )
          .then((res) => setFilteredToEmployees(res.data))
          .catch((err) => console.error("Error fetching employees:", err));
      }
    }

    setTransferData(newData);
  };

  const fetchEmployees = async (division, stateId, isFrom) => {
    if (!division || !stateId) {
      console.warn("Missing division or state for employee fetch");
      if (isFrom) {
        setFilteredFromEmployees([]);
      } else {
        setFilteredToEmployees([]);
      }
      return;
    }

    try {
      const res = await axios.get(
        `${BASE_URL}/api/employees?state_id=${stateId}&division=${encodeURIComponent(
          division,
        )}`,
        { headers: getAuthHeaders() },
      );
      if (isFrom) {
        setFilteredFromEmployees(res.data);
      } else {
        setFilteredToEmployees(res.data);
      }
    } catch (err) {
      console.error("Error fetching employees:", err);
      if (isFrom) {
        setFilteredFromEmployees([]);
      } else {
        setFilteredToEmployees([]);
      }
    }
  };

  const handleTransferRoutes = async () => {
    if (!selectedTransferRoutes.length || !transferData.toEmployee) {
      alert("Please select routes and a destination employee");
      return;
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/api/routes/transfer`,
        {
          route_ids: selectedTransferRoutes,
          to_employee_id: transferData.toEmployee,
        },
        {
          headers: getAuthHeaders(),
        },
      );

      alert(
        `Successfully transferred ${response.data.transferred_routes.length} routes to ${transferData.toEmployee}`,
      );
      setTransferMode(false);
      setSelectedTransferRoutes([]);
      setActiveOperation(null);
      handleGoClick();
    } catch (error) {
      console.error("Transfer error:", error);
      const errorMsg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message;
      alert(`Transfer failed: ${errorMsg}`);
    }
  };

  const handleCancelTransfer = () => {
    setTransferMode(false);
    setSelectedTransferRoutes([]);
    setActiveOperation(null);
    setTransferData({
      fromDivision: "",
      toDivision: "",
      fromEmployee: "",
      toEmployee: "",
      state: "",
    });
    setFilteredFromEmployees([]);
    setFilteredToEmployees([]);
  };

  // NEW: refactored prepareMergeRoutes that accepts the selected ids
  const prepareMergeRoutes = async (selectedIds) => {
    if (!selectedIds || selectedIds.length < 2) {
      setMergePreview(null);
      return;
    }

    const routesToMerge = routes.filter((route) =>
      selectedIds.includes(route.id),
    );

    const totals = {
      distance: routesToMerge.reduce(
        (sum, route) => sum + (route.distance_from_hq || 0),
        0,
      ),
      doctors: routesToMerge.reduce(
        (sum, route) => sum + (route.no_of_doctors || 0),
        0,
      ),
      chemists: routesToMerge.reduce(
        (sum, route) => sum + (route.no_of_chemist || 0),
        0,
      ),
      stockists: routesToMerge.reduce(
        (sum, route) => sum + (route.no_of_stockist || 0),
        0,
      ),
      tp_visit_cycle: routesToMerge[0]?.tp_visit_cycle_per_month,
    };

    try {
      // Fetch cities for the first route's state
      const citiesRes = await axios.get(
        `${BASE_URL}/api/cities/${routesToMerge[0].state_id}`,
        {
          headers: getAuthHeaders(),
        },
      );
      setFilteredCities(citiesRes.data);

      // Fetch headquarters for the first route's city
      const hqRes = await axios.get(
        `${BASE_URL}/api/headquarters/${routesToMerge[0].city_id}`,
        {
          headers: getAuthHeaders(),
        },
      );
      setFilteredHeadquarters(hqRes.data);

      setMergePreview({
        routes: routesToMerge,
        totals,
      });

      setMergedRouteData({
        route_name: "",
        state_id: routesToMerge[0].state_id,
        city_id: routesToMerge[0].city_id,
        headquarter_id: routesToMerge[0].headquarter_id,
        route_type_id: routesToMerge[0].route_type_id,
        distance_from_hq: totals.distance,
        no_of_doctors: totals.doctors,
        no_of_chemist: totals.chemists,
        no_of_stockist: totals.stockists,
        tp_visit_cycle_per_month: totals.tp_visit_cycle,
        division_id: routesToMerge[0]?.division_id,
        area_id: routesToMerge[0]?.area_id,
      });
    } catch (error) {
      console.error("Error fetching location data:", error);
      alert("Error loading location data for merging");
    }
  };

  const handleCancelMerge = () => {
    setMergePreview(null);
    setSelectedRoutes([]);
    setActiveOperation(null);
    setFilteredCities([]);
    setFilteredHeadquarters([]);
  };

  const handleSaveMergedRoute = async () => {
    if (!mergedRouteData.route_name.trim()) {
      alert("Please enter a name for the merged route");
      return;
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/api/routes/merge`,
        {
          route_name: mergedRouteData.route_name,
          employee_id: selectedEmployee,
          state_id: mergedRouteData.state_id,
          city_id: mergedRouteData.city_id,
          headquarter_id: mergedRouteData.headquarter_id,
          route_type_id: mergedRouteData.route_type_id,
          distance_from_hq: Number(mergedRouteData.distance_from_hq) || 0,
          route_ids: mergePreview.routes.map((route) => route.id),
          division_id: mergePreview.routes[0]?.division_id,
          area_id: mergePreview.routes[0]?.area_id,
          no_of_doctors: mergePreview.totals.doctors || 0,
          no_of_chemist: mergePreview.totals.chemists || 0,
          no_of_stockist: mergePreview.totals.stockists || 0,
        },

        {
          headers: getAuthHeaders(),
        },
      );

      alert(
        `Routes merged successfully! New route code: ${response.data.route_code}`,
      );
      handleCancelMerge();
      handleGoClick();
    } catch (error) {
      console.error("Merge error:", error);
      alert(`Merge failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleMergeInputChange = async (e, field) => {
    const value = e.target.value;
    const newData = {
      ...mergedRouteData,
      [field]:
        value === "" ? "" : field.endsWith("_id") ? parseInt(value) : value,
    };

    if (field === "state_id") {
      newData.city_id = "";
      newData.headquarter_id = "";
      if (value) {
        try {
          const res = await axios.get(`${BASE_URL}/api/cities/${value}`, {
            headers: getAuthHeaders(),
          });
          setFilteredCities(res.data);
        } catch (error) {
          console.error("Error fetching cities:", error);
        }
      }
    }

    if (field === "city_id") {
      newData.headquarter_id = "";
      if (value) {
        try {
          const res = await axios.get(`${BASE_URL}/api/headquarters/${value}`, {
            headers: getAuthHeaders(),
          });
          setFilteredHeadquarters(res.data);
        } catch (error) {
          console.error("Error fetching headquarters:", error);
        }
      }
    }

    setMergedRouteData(newData);
  };

  const handleSelectRoute = (routeId) => {
    // If there's an active operation and it's not merge, do nothing
    if (activeOperation && activeOperation !== "merge") return;

    // Build newSelection synchronously so we can trigger prepareMergeRoutes immediately
    setSelectedRoutes((prev) => {
      const newSelection = prev.includes(routeId)
        ? prev.filter((id) => id !== routeId)
        : [...prev, routeId];

      // If no routes are selected, clear the active operation and merge preview
      if (newSelection.length === 0) {
        setActiveOperation(null);
        setMergePreview(null);
      } else {
        setActiveOperation("merge");
      }

      if (newSelection.length >= 2) {
        prepareMergeRoutes(newSelection);
      } else {
        setMergePreview(null);
      }

      return newSelection;
    });
  };

  const openSmallWindow = (type, routeId) => {
    window.open(
      `/admin/${type}`,
      "_blank",
      "width=1100,height=700,top=100,left=100",
    );
  };

  // Fetch all initial data
  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/states`, {
        headers: getAuthHeaders(),
      })
      .then((res) => setStates(res.data))
      .catch((err) => console.error("Error fetching states:", err));

    axios
      .get(`${BASE_URL}/api/cities`, {
        headers: getAuthHeaders(),
      })
      .then((res) => setAllCities(res.data))
      .catch((err) => console.error("Error fetching cities:", err));

    axios
      .get(`${BASE_URL}/api/headquarters`, {
        headers: getAuthHeaders(),
      })
      .then((res) => setAllHeadquarters(res.data))
      .catch((err) => console.error("Error fetching headquarters:", err));

    axios
      .get(`${BASE_URL}/api/route_types`, {
        headers: getAuthHeaders(),
      })
      .then((res) => setAllRouteTypes(res.data))
      .catch((err) => console.error("Error fetching route types:", err));
  }, []);

  const handleStateChange = (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("");
    setSelectedEmployee("");
    setDivisions([]);
    setEmployees([]);
    setShowTable(false);

    axios
      .get(`${BASE_URL}/api/divisions?state_id=${stateId}`, {
        headers: getAuthHeaders(),
      })
      .then((res) => {
        setDivisions(res.data);
        if (transferMode || selectedTransferRoutes.length > 0) {
          setFilteredToDivisions(res.data);
        }
      })
      .catch((err) => console.error("Error fetching divisions:", err));
  };

  const handleDivisionChange = (divisionName) => {
    setSelectedDivision(divisionName);
    setSelectedEmployee("");
    setEmployees([]);
    setShowTable(false);

    if (!selectedState || !divisionName) return;

    axios
      .get(
        `${BASE_URL}/api/employees?state_id=${selectedState}&division=${encodeURIComponent(
          divisionName,
        )}`,
        {
          headers: getAuthHeaders(),
        },
      )
      .then((res) => {
        setEmployees(res.data);
      })
      .catch((err) => console.error("Error fetching employees:", err));
  };

  const handleGoClick = () => {
    if (!selectedEmployee) {
      alert("Please select an employee.");
      return;
    }

    axios
      .get(`${BASE_URL}/api/previous_routes/employee/${selectedEmployee}`, {
        headers: getAuthHeaders(),
      })
      .then((res) => {
        setRoutes(res.data);
        setShowTable(true);
      })
      .catch((err) => {
        console.error("Error fetching routes:", err);
        alert(
          `Error loading routes: ${
            err.response?.data?.message || "No routes found for this employee"
          }`,
        );
        setRoutes([]);
      });
  };

  const handleEditClick = async (index) => {
    const route = routes[index];
    setEditableRow(index);

    try {
      const citiesRes = await axios.get(
        `${BASE_URL}/api/cities/${route.state_id}`,
        {
          headers: getAuthHeaders(),
        },
      );
      setFilteredCities(citiesRes.data);

      const hqRes = await axios.get(
        `${BASE_URL}/api/headquarters/${route.city_id}`,
        {
          headers: getAuthHeaders(),
        },
      );
      setFilteredHeadquarters(hqRes.data);

      setEditedData({
        route_name: route.route_name || "",
        state_id: route.state_id || "",
        city_id: route.city_id || "",
        headquarter_id: route.headquarter_id || "",
        route_type_id: route.route_type_id || "",
        distance_from_hq: route.distance_from_hq || 0,
      });
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  const handleInputChange = async (e, field) => {
    const value = e.target.value;
    const newData = {
      ...editedData,
      [field]:
        value === "" ? "" : field.endsWith("_id") ? parseInt(value) : value,
    };

    if (field === "state_id") {
      newData.city_id = "";
      newData.headquarter_id = "";
      setFilteredHeadquarters([]);
      if (value) {
        try {
          const res = await axios.get(`${BASE_URL}/api/cities/${value}`, {
            headers: getAuthHeaders(),
          });
          setFilteredCities(res.data);
        } catch (error) {
          console.error("Error fetching cities:", error);
        }
      } else {
        setFilteredCities([]);
      }
    }

    if (field === "city_id") {
      newData.headquarter_id = "";
      if (value) {
        try {
          const res = await axios.get(`${BASE_URL}/api/headquarters/${value}`, {
            headers: getAuthHeaders(),
          });
          setFilteredHeadquarters(res.data);
        } catch (error) {
          console.error("Error fetching headquarters:", error);
        }
      } else {
        setFilteredHeadquarters([]);
      }
    }

    setEditedData(newData);
  };

  const handleSaveClick = async (routeId) => {
    try {
      const dataToUpdate = {
        route_name: editedData.route_name,
        state_id: editedData.state_id,
        city_id: editedData.city_id,
        headquarter_id: editedData.headquarter_id,
        route_type_id: editedData.route_type_id,
        distance_from_hq:
          editedData.distance_from_hq === ""
            ? null
            : parseFloat(editedData.distance_from_hq),
        added_date: routes.find((r) => r.id === routeId)?.added_date,
        employee_id: selectedEmployee,
        active: true,
      };

      const response = await axios.put(
        `${BASE_URL}/api/routes/${routeId}`,
        dataToUpdate,
        {
          headers: getAuthHeaders(),
        },
      );

      alert("Route updated successfully!");
      setEditableRow(null);
      handleGoClick();
    } catch (error) {
      console.error("Error updating route:", error);
      alert(
        `Failed to update route: ${
          error.response?.data?.detail || error.message
        }`,
      );
    }
  };

  const renderEditableCell = (
    index,
    field,
    value,
    isDropdown = false,
    options = [],
  ) => {
    if (editableRow === index) {
      if (isDropdown) {
        return (
          <select
            value={editedData[`${field}_id`] || editedData[field]}
            onChange={(e) => handleInputChange(e, `${field}_id`)}
          >
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        );
      }
      return (
        <input
          type={typeof value === "number" ? "number" : "text"}
          value={editedData[field]}
          onChange={(e) => handleInputChange(e, field)}
        />
      );
    }

    if (isDropdown) {
      const nameField = routes[index][field];
      if (nameField) return nameField;
      return value;
    }
    return value;
  };

  const handleCancelOperation = () => {
    setActiveOperation(null);
    setSelectedRoutes([]);
    setSelectedTransferRoutes([]);
    setSelectedCopyRoutes([]);
    setSelectedDeleteRoutes([]);
    setMergePreview(null);
    setTransferMode(false);
    setCopyMode(false);
    setDeleteConfirmation(false);
  };

  return (
    <div className="edit-employee-routes">
      <div className="p-2 border-2 border-gray-300 rounded-lg bg-white shadow-md">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">Route </span>
          <span className="text-blue-500">Working</span>
        </h1>

        <div className="filter-row1">
          <label className="editroutes-filter-label">State</label>
          <select
            className="editroutes-dropdown"
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
          >
            <option value="">Select State</option>
            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>

          <label className="editroutes-filter-label">Division</label>
          <select
            className="editroutes-dropdown"
            value={selectedDivision}
            onChange={(e) => handleDivisionChange(e.target.value)}
          >
            <option value="">Select Division</option>
            {divisions.map((division) => (
              <option key={division.id} value={division.name}>
                {division.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-row2">
          <label className="editroutes-filter-label">Employee</label>
          <select
            className="editroutes-dropdown"
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            disabled={!selectedDivision}
          >
            <option value="">Select Employee</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.first_name} {emp.last_name}  | {emp.employee} ({emp.designation}|
                {emp.headquarter_name}|{emp.division}|{emp.state_name})
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-center">
          <CustomButton variant="go" onClick={handleGoClick}>
            GO
          </CustomButton>
        </div>
      </div>

      <div className="action-container">
        {selectedDeleteRoutes.length > 0 && (
          <CustomButton
            onClick={() => setDeleteConfirmation(true)}
            className="delete-btn"
            disabled={activeOperation && activeOperation !== "delete"}
          >
            Delete Routes ({selectedDeleteRoutes.length})
          </CustomButton>
        )}
      </div>

      {mergePreview && (
        <div className="routes-table-container">
          <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
            <span className="text-blue-700">Merge </span>
            <span className="text-blue-500">Routes</span>
            {/* Count badge */}
            <span className="ml-3 inline-block  text-gray-800 rounded-full px-3 py-1 text-sm font-medium">
              Selected:{" "}
              {(mergePreview.routes && mergePreview.routes.length) ||
                selectedRoutes.length ||
                0}
            </span>
          </h1>

          <table className="unlock-dcr-leave-table text-sm ">
            <colgroup>
              <col style={{ width: "5%" }} /> {/* S.No */}
              <col style={{ width: "30%" }} /> {/* Route Name - LARGE */}
              <col style={{ width: "20%" }} /> {/* State / City / HQ */}
              <col style={{ width: "15%" }} /> {/* Route Type */}
              <col style={{ width: "8%" }} /> {/* Distance */}
              <col style={{ width: "20%" }} /> {/* Action */}
            </colgroup>

            <thead className="bg-blue-500 text-white">
              <tr>
                <th>S.No</th>
                <th>Route Name</th>
                <th className="stacked-header">
                  <div>State</div>
                  <div>City</div>
                  <div>Headquarter</div>
                </th>
                <th>Route Type</th>
                <th>Distance (KM)</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>1</td>
                <td>
                  <input
                    type="text"
                    value={mergedRouteData.route_name}
                    onChange={(e) =>
                      setMergedRouteData({
                        ...mergedRouteData,
                        route_name: e.target.value,
                      })
                    }
                    className="route-name-input"
                    required
                  />
                </td>

                <td className="combined-location-cell">
                  {/* State Dropdown */}
                  <div>
                    <select
                      value={mergedRouteData.state_id}
                      onChange={(e) => handleMergeInputChange(e, "state_id")}
                      className="location-select"
                    >
                      <option value="">Select State</option>
                      {states.map((state) => (
                        <option key={state.id} value={state.id}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* City Dropdown */}
                  <div>
                    <select
                      value={mergedRouteData.city_id}
                      onChange={(e) => handleMergeInputChange(e, "city_id")}
                      className="location-select"
                      disabled={!mergedRouteData.state_id}
                    >
                      <option value="">Select City</option>
                      {filteredCities.map((city) => (
                        <option key={city.id} value={city.id}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Headquarter Dropdown */}
                  <div>
                    <select
                      value={mergedRouteData.headquarter_id}
                      onChange={(e) =>
                        handleMergeInputChange(e, "headquarter_id")
                      }
                      disabled={!mergedRouteData.city_id}
                    >
                      <option value="">Select HQ</option>
                      {filteredHeadquarters.map((hq) => (
                        <option key={hq.id} value={hq.id}>
                          {hq.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </td>

                <td>
                  <select
                    value={mergedRouteData.route_type_id}
                    onChange={(e) => handleMergeInputChange(e, "route_type_id")}
                  >
                    {allRouteTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </td>

                <td>
                  <input
                    type="number"
                    value={mergedRouteData.distance_from_hq ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (value === "") {
                        setMergedRouteData({
                          ...mergedRouteData,
                          distance_from_hq: "",
                        });
                        return;
                      }

                      if (value.length > 3) return;

                      setMergedRouteData({
                        ...mergedRouteData,
                        distance_from_hq: Number(value),
                      });
                    }}
                    className="distance-input border px-2 py-1 rounded w-full"
                    min={0}
                    max={999}
                    placeholder="Enter distance (KM)"
                  />
                </td>

                <td>
                  <div className="merge-button-group">
                    <CustomButton
                      variant="save"
                      onClick={handleSaveMergedRoute}
                    >
                      Save
                    </CustomButton>
                    <CustomButton variant="cancel" onClick={handleCancelMerge}>
                      Cancel
                    </CustomButton>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {(transferMode || selectedTransferRoutes.length > 0) && (
        <div className="transfer-container">
          <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800">
            <span className="text-blue-700">Transfer </span>
            <span className="text-blue-500">Routes</span>
          </h1>
          {/* Set up a 3-column grid for transfer form */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* First Column */}
            <div className="transfer-row1">
              <label className="transfer-data-label">To State:</label>
              <select
                className="transfer-data-dropdown"
                value={transferData.toState}
                onChange={(e) => handleTransferInputChange(e, "toState")}
              >
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Second Column */}
            <div className="transfer-row2">
              <label className="transfer-data-label">To Division:</label>
              <select
                className="transfer-data-dropdown"
                value={transferData.toDivision}
                onChange={async (e) => {
                  const divisionName = e.target.value;
                  setTransferData((prev) => ({
                    ...prev,
                    toDivision: divisionName,
                    toEmployee: "",
                  }));

                  if (transferData.toState && divisionName) {
                    try {
                      const response = await axios.get(
                        `${BASE_URL}/api/employees`,
                        {
                          params: {
                            state_id: transferData.toState,
                            division: divisionName,
                          },
                          headers: getAuthHeaders(),
                        },
                      );
                      setFilteredToEmployees(response.data);
                    } catch (error) {
                      console.error("Error fetching employees:", error);
                      setFilteredToEmployees([]);
                    }
                  }
                }}
                disabled={!transferData.toState}
              >
                <option value="">Select Division</option>
                {filteredToDivisions.map((div) => (
                  <option key={div.id} value={div.name}>
                    {div.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Third Column */}
            <div className="transfer-row3">
              <label className="transfer-data-label">To Employee:</label>
              <select
                className="transfer-data-dropdown"
                value={transferData.toEmployee}
                onChange={(e) =>
                  setTransferData((prev) => ({
                    ...prev,
                    toEmployee: e.target.value,
                  }))
                }
                disabled={!transferData.toDivision}
              >
                <option value="">Select Employee</option>
                {filteredToEmployees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name} | {emp.employee} | {emp.designation}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="transfer-actions mt-4 flex justify-center gap-4">
            <button
              className="transfer-btn px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              onClick={handleTransferRoutes}
              disabled={!transferData.toEmployee}
            >
              Transfer Routes ({selectedTransferRoutes.length})
            </button>
            <button
              className="transfer-cancel-btn px-6 py-2 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700"
              onClick={handleCancelTransfer}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {copyMode && (
        <div className="copy-container">
          <div className="copy-title3-container">
            <h3 className="copy-data-title3 whitespace-nowrap text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
              <span className="text-blue-700">Copy </span>
              <span className="text-blue-500">Routes</span> (
              {selectedCopyRoutes.length})
            </h3>
          </div>

          <div className="copy-row1">
            <label className="copy-data-label"> With Customers </label>
            <input
              type="radio"
              className="copy-data-dropdown"
              checked={copyData.withCustomers}
              onChange={() => setCopyData({ ...copyData, withCustomers: true })}
            />
            <label className="copy-data-label"> Without Customers </label>
            <input
              type="radio"
              className="copy-data-dropdown"
              checked={!copyData.withCustomers}
              onChange={() =>
                setCopyData({ ...copyData, withCustomers: false })
              }
            />
          </div>

          <div className="copy-row2">
            <label className="copy-data-label">To State:</label>
            <select
              value={copyData.toState}
              onChange={(e) => {
                const stateId = e.target.value;
                setCopyData({
                  ...copyData,
                  toState: stateId,
                  toDivision: "",
                  toEmployee: "",
                });
              }}
            >
              <option value="">Select State</option>
              {states.map((state) => (
                <option key={state.id} value={state.id}>
                  {state.name}
                </option>
              ))}
            </select>

            <label className="copy-data-label">To Division:</label>
            <select
              value={copyData.toDivision}
              onChange={async (e) => {
                const divisionName = e.target.value;
                setCopyData({
                  ...copyData,
                  toDivision: divisionName,
                  toEmployee: "",
                });

                if (copyData.toState && divisionName) {
                  const response = await axios.get(
                    `${BASE_URL}/api/employees`,
                    {
                      params: {
                        state_id: copyData.toState,
                        division: divisionName,
                      },
                      headers: getAuthHeaders(),
                    },
                  );
                  setFilteredToEmployees(response.data);
                }
              }}
              disabled={!copyData.toState}
            >
              <option value="">Select Division</option>
              {divisions.map((div) => (
                <option key={div.id} value={div.name}>
                  {div.name}
                </option>
              ))}
            </select>
          </div>

          <div className="copy-row3">
            <label className="copy-data-label">To Employee:</label>
            <select
              value={copyData.toEmployee}
              onChange={(e) =>
                setCopyData({
                  ...copyData,
                  toEmployee: e.target.value,
                })
              }
              disabled={!copyData.toDivision}
            >
              <option value="">Select Employee</option>
              {filteredToEmployees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.first_name} {emp.last_name}
                </option>
              ))}
            </select>
          </div>

          <div className="copy-actions">
            <button
              className="copy-btn"
              onClick={handleCopyRoutes}
              disabled={!copyData.toEmployee}
            >
              Confirm
            </button>
            <button className="cancel-btn" onClick={handleCancelCopy}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {deleteConfirmation && (
        <div className="delete-confirmation-modal">
          <div className="delete-confirmation-content">
            <h3>Confirm Delete</h3>
            <p>
              Are you sure you want to delete {selectedDeleteRoutes.length}{" "}
              selected routes?
            </p>
            <div className="delete-confirmation-buttons">
              <button
                className="confirm-delete-btn"
                onClick={handleDeleteRoutes}
              >
                Yes, Delete
              </button>
              <button
                className="cancel-delete-btn"
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showTable && (
        <div className="p-2 border-2 border-gray-300 rounded-lg bg-white shadow-md table-wrapper">
          <table className="unlock-dcr-leave-table text-sm ">
            <thead className="bg-blue-500 text-white">
              <tr>
             {!isExpenseUser && (
                  <>
                    <th>
                      Merge
                      <br />
                      Routes
                      <br />
                      <input
                        type="checkbox"
                        checked={
                          selectedRoutes.length === routes.length &&
                          routes.length > 0 &&
                          activeOperation === "merge"
                        }
                        onChange={(e) => {
                          if (activeOperation && activeOperation !== "merge")
                            return;

                          if (e.target.checked) {
                            setActiveOperation("merge");
                            setSelectedRoutes(routes.map((r) => r.id));
                            prepareMergeRoutes(routes.map((r) => r.id));
                          } else {
                            setActiveOperation(null);
                            setSelectedRoutes([]);
                            setMergePreview(null);
                          }
                        }}
                        disabled={
                          activeOperation && activeOperation !== "merge"
                        }
                      />
                    </th>

                    <th>
                      Transfer
                      <br />
                      Routes
                      <br />
                      <input
                        type="checkbox"
                        checked={
                          selectedTransferRoutes.length === routes.length &&
                          routes.length > 0 &&
                          activeOperation === "transfer"
                        }
                        onChange={(e) => {
                          if (activeOperation && activeOperation !== "transfer")
                            return;

                          if (e.target.checked) {
                            setActiveOperation("transfer");
                            setSelectedTransferRoutes(routes.map((r) => r.id));
                            setTransferMode(true);
                          } else {
                            setActiveOperation(null);
                            setSelectedTransferRoutes([]);
                            setTransferMode(false);
                          }
                        }}
                        disabled={
                          activeOperation && activeOperation !== "transfer"
                        }
                      />
                    </th>

                    <th>
                      Copy
                      <br />
                      Routes
                      <br />
                      <input
                        type="checkbox"
                        checked={
                          selectedCopyRoutes.length === routes.length &&
                          routes.length > 0 &&
                          activeOperation === "copy"
                        }
                        onChange={(e) => {
                          if (activeOperation && activeOperation !== "copy")
                            return;

                          if (e.target.checked) {
                            setActiveOperation("copy");
                            setSelectedCopyRoutes(routes.map((r) => r.id));
                            setCopyMode(true);
                          } else {
                            setActiveOperation(null);
                            setSelectedCopyRoutes([]);
                            setCopyMode(false);
                          }
                        }}
                        disabled={activeOperation && activeOperation !== "copy"}
                      />
                    </th>

                    <th>
                      Delete
                      <br />
                      Routes
                      <br />
                      <input
                        type="checkbox"
                        checked={
                          selectedDeleteRoutes.length === routes.length &&
                          routes.length > 0 &&
                          activeOperation === "delete"
                        }
                        onChange={(e) => {
                          if (activeOperation && activeOperation !== "delete")
                            return;

                          if (e.target.checked) {
                            setActiveOperation("delete");
                            setSelectedDeleteRoutes(routes.map((r) => r.id));
                          } else {
                            setActiveOperation(null);
                            setSelectedDeleteRoutes([]);
                          }
                        }}
                        disabled={
                          activeOperation && activeOperation !== "delete"
                        }
                      />
                    </th>
                  </>
                )}
                <th>S.No</th>
                <th>Route Code</th>
                <th className="w-32">Route Name</th>
                <th>Added Date</th>
                <th>Latest Visit</th>
                <th className="stacked-header">
                  <div>State</div>
                  <div>City</div>
                  <div>Headquarter</div>
                </th>
                <th>Route Type</th>
                <th className="w-5">
                  Distance
                  <br />
                  From HQ
                  <br />
                  (One Way)
                </th>
                {/* <th>Active</th> */}
                <th>Doctors</th>
                <th>Chemists</th>
                <th>Stockists</th>
                <th className="w-1">Update</th>
              </tr>
            </thead>
            <tbody>
              {routes.map((route, index) => (
                <tr key={index}>
                 {!isExpenseUser && (
                    <>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedRoutes.includes(route.id)}
                          onChange={() => handleSelectRoute(route.id)}
                          disabled={
                            activeOperation && activeOperation !== "merge"
                          }
                        />
                      </td>

                      <td>
                        <input
                          type="checkbox"
                          checked={selectedTransferRoutes.includes(route.id)}
                          onChange={() => handleTransferCheckbox(route.id)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={
                            activeOperation && activeOperation !== "transfer"
                          }
                        />
                      </td>

                      <td>
                        <input
                          type="checkbox"
                          checked={selectedCopyRoutes.includes(route.id)}
                          onChange={() => handleCopyCheckbox(route.id)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={
                            activeOperation && activeOperation !== "copy"
                          }
                        />
                      </td>

                      <td>
                        <input
                          type="checkbox"
                          checked={selectedDeleteRoutes.includes(route.id)}
                          onChange={() => handleDeleteCheckbox(route.id)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={
                            activeOperation && activeOperation !== "delete"
                          }
                        />
                      </td>
                    </>
                  )}
                  <td>{index + 1}</td>
                  <td>{route.route_code}</td>
                  <td>
                    {editableRow === index ? (
                      <input
                        type="text"
                        value={editedData.route_name}
                        onChange={(e) => handleInputChange(e, "route_name")}
                        className="border border-gray-600 rounded px-1"
                      />
                    ) : (
                      route.route_name
                    )}
                  </td>
                  <td>{formatDate(route.added_date) || route.added_date}</td>
                  <td>
                    {formatDate(route.latest_visit_date) ||
                      route.latest_visit_date}
                  </td>
                  <td className="combined-location-cell">
                    {editableRow === index ? (
                      <>
                        <div>
                          <select
                            value={editedData.state_id || ""}
                            onChange={(e) => handleInputChange(e, "state_id")}
                            className="location-select"
                          >
                            {states.map((state) => (
                              <option key={state.id} value={state.id}>
                                {state.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <select
                            value={editedData.city_id || ""}
                            onChange={(e) => handleInputChange(e, "city_id")}
                            className="location-select"
                            disabled={!editedData.state_id}
                          >
                            <option value="">Select City</option>
                            {filteredCities.map((city) => (
                              <option key={city.id} value={city.id}>
                                {city.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <select
                            value={editedData.headquarter_id || ""}
                            onChange={(e) =>
                              handleInputChange(e, "headquarter_id")
                            }
                            className="location-select"
                            disabled={!editedData.city_id}
                          >
                            <option value="">Select Headquarter</option>
                            {filteredHeadquarters.map((hq) => (
                              <option key={hq.id} value={hq.id}>
                                {hq.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>{route.state}</div>
                        <div>{route.city}</div>
                        <div>{route.headquarter}</div>
                      </>
                    )}
                  </td>
                  <td>
                    {renderEditableCell(
                      index,
                      "route_type",
                      route.route_type,
                      true,
                      allRouteTypes,
                    )}
                  </td>
                  <td>
                    {editableRow === index ? (
                      <input
                        className="border border-gray-600 rounded px-1 w-11"
                        type="number"
                        step="0.1"
                        value={editedData.distance_from_hq ?? ""}
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          if (rawValue === "") {
                            setEditedData((prev) => ({
                              ...prev,
                              distance_from_hq: "",
                            }));
                            return;
                          }
                          if (rawValue.length > 3) return;
                          setEditedData((prev) => ({
                            ...prev,
                            distance_from_hq: Number(rawValue),
                          }));
                        }}
                      />
                    ) : (
                      route.distance_from_hq
                    )}
                  </td>
                  {/* <td>
                    {route.active ? (
                      <span className="active-indicator">✓</span>
                    ) : (
                      <span className="inactive-indicator">✗</span>
                    )}
                  </td> */}
                  <td
                    className="text-blue-600 cursor-pointer underline text-center"
                    onClick={() =>
                      openSmallWindow(
                        `doctors/${route.id}?employee_id=${selectedEmployee}`,
                      )
                    }
                  >
                    {route.doctor_count}
                  </td>

                  <td
                    className="text-blue-600 cursor-pointer underline text-center"
                    onClick={() =>
                      openSmallWindow(
                        `chemists/${route.id}?employee_id=${selectedEmployee}`,
                      )
                    }
                  >
                    {route.chemist_count}
                  </td>

                  <td
                    className="text-blue-600 cursor-pointer underline text-center"
                    onClick={() =>
                      openSmallWindow(
                        `stockists/${route.id}?employee_id=${selectedEmployee}&count=${route.stockist_count}`,
                      )
                    }
                  >
                    {route.stockist_count}
                  </td>

                  <td>
                    {editableRow === index ? (
                      <div className="button-group">
                        <CustomButton
                          variant="save"
                          onClick={() => handleSaveClick(route.id)}
                        >
                          Save
                        </CustomButton>
                        <CustomButton
                          variant="cancel"
                          onClick={() => setEditableRow(null)}
                        >
                          Cancel
                        </CustomButton>
                      </div>
                    ) : (
                      <CustomButton
                        variant="edit"
                        onClick={() => handleEditClick(index)}
                      >
                        Edit
                      </CustomButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-blue-500 text-white font-semibold">
                {/* Cover all columns before Doctors */}
                <td colSpan={isExpenseUser ? 8 : 12}>Total</td>

                {/* Doctors */}
                <td className="text-center">{totalDoctors}</td>

                {/* Chemists */}
                <td className="text-center">{totalChemists}</td>

                {/* Stockists */}
                <td className="text-center">{totalStockists}</td>

                {/* Update column (empty) */}
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default EditEmployeeRoutes;
